import { useEffect, useRef, useState } from "react";
import { CalendarDays, Moon, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../lib/i18n";
import PriceCalendar from "./PriceCalendar";

const inputCls =
  "w-full bg-transparent text-white text-[13px] font-sans outline-none placeholder-white/50 [color-scheme:dark]";

// Extract the Exely hotel code (providerId) from the configured booking URL
const hotelCodeFromUrl = (url) => {
  const match = String(url || "").match(/[?&]providerId=(\d+)/);
  return match ? match[1] : "";
};

export default function BookingWidget({ bookingUrl }) {
  const { t, lang } = useLang();
  const [checkin, setCheckin] = useState("");
  const [nights, setNights] = useState("");
  const [adults, setAdults] = useState("2");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  const hotelCode = hotelCodeFromUrl(bookingUrl);

  useEffect(() => {
    if (!calendarOpen) return undefined;
    const onPointerDown = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) setCalendarOpen(false);
    };
    const onEscape = (event) => {
      if (event.key === "Escape") setCalendarOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [calendarOpen]);

  const openBooking = () => {
    // Dates/guests ride into the embedded engine on /book - it auto-searches and lands
    // straight on availability results without asking for the dates again
    if (!bookingUrl) {
      toast.info(t("book.notConfigured"));
      return;
    }
    const params = new URLSearchParams();
    if (checkin) params.set("date", checkin);
    if (nights) params.set("nights", nights);
    if (adults) params.set("adults", adults);
    const qs = params.toString();
    window.location.href = `/book${qs ? `?${qs}` : ""}`;
  };

  const checkinLabel = checkin
    ? new Date(`${checkin}T00:00:00`).toLocaleDateString(lang === "id" ? "id-ID" : "en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  const field = (icon, label, control, testId) => (
    <label data-testid={testId} className="flex items-center gap-2.5 px-4 py-2 flex-1 min-w-[130px]">
      <span className="text-[#EADFD3] shrink-0">{icon}</span>
      <span className="flex flex-col items-start w-full">
        <span className="text-[9px] uppercase tracking-[0.14em] font-sans font-semibold text-white/60">{label}</span>
        {control}
      </span>
    </label>
  );

  return (
    <div data-testid="hero-booking-widget" className="flex justify-center w-full px-2">
      <div ref={calendarRef} className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-1 sm:gap-0 bg-white/15 backdrop-blur-md border border-white/40 rounded-2xl sm:rounded-full p-1.5 shadow-[0_10px_30px_rgba(30,26,23,0.3)] w-full max-w-2xl sm:divide-x sm:divide-white/20">
        <div className="relative flex-1 min-w-[130px]">
          <button
            type="button"
            data-testid="booking-checkin-input"
            onClick={() => setCalendarOpen((v) => !v)}
            aria-expanded={calendarOpen}
            className="flex items-center gap-2.5 px-4 py-2 w-full text-left cursor-pointer"
          >
            <span className="text-[#EADFD3] shrink-0">
              <CalendarDays className="w-4 h-4" />
            </span>
            <span className="flex flex-col items-start w-full">
              <span className="text-[9px] uppercase tracking-[0.14em] font-sans font-semibold text-white/60">{t("book.checkin")}</span>
              <span className={`${inputCls} ${checkinLabel ? "" : "text-white/50"}`}>{checkinLabel || t("book.selectDate")}</span>
            </span>
          </button>
        </div>
        {calendarOpen && (
          <div className="absolute bottom-[calc(100%+12px)] left-1/2 -translate-x-1/2 z-[80] max-h-[70svh] overflow-y-auto rounded-xl">
            <PriceCalendar
              hotelCode={hotelCode}
              value={checkin}
              lang={lang}
              onSelect={(iso) => {
                setCheckin(iso);
                setCalendarOpen(false);
              }}
            />
          </div>
        )}
        {field(
          <Moon className="w-4 h-4" />,
          t("book.nights"),
          <input
            data-testid="booking-nights-input"
            type="number"
            min="1"
            max="30"
            placeholder="3"
            value={nights}
            onChange={(e) => setNights(e.target.value)}
            className={inputCls}
          />,
          "booking-field-nights"
        )}
        {field(
          <Users className="w-4 h-4" />,
          t("book.guests"),
          <select
            data-testid="booking-adults-select"
            value={adults}
            onChange={(e) => setAdults(e.target.value)}
            className={`${inputCls} cursor-pointer`}
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n} className="text-[#2C2825]">
                {n}
              </option>
            ))}
          </select>,
          "booking-field-guests"
        )}
        <button
          data-testid="booking-widget-toggle"
          onClick={openBooking}
          className="group inline-flex items-center justify-center gap-1.5 bg-[#FBF9F5] text-[#2C2825] hover:bg-white rounded-xl sm:rounded-full px-6 py-3 sm:py-2.5 text-[11px] font-sans font-semibold uppercase tracking-[0.1em] transition-colors whitespace-nowrap"
        >
          {t("book.cta")}
          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
}
